import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  Spin,
  Space,
  Badge,
} from 'antd';
import {
  DollarOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { config } from '../config/env';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface ContributorProfile {
  id: string;
  type: string;
  status: string;
  rating: number;
  totalTasks: number;
  completedTasks: number;
  creditsEarned: number;
  totalEarnings: number;
  verified: boolean;
  certifications: string[];
}

interface ContributorTask {
  id: string;
  type: string;
  status: string;
  priority: string;
  title: string;
  description: string;
  creditsReward: number;
  paymentAmount?: number;
  qualityScore?: number;
}

export const ContributorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ContributorProfile | null>(null);
  const [tasks, setTasks] = useState<ContributorTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${config.apiUrl}/api/contributors/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No profile yet
        setProfile(null);
      }
    }
  };

  const fetchTasks = async () => {
    if (!profile) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${config.apiUrl}/api/contributors/tasks/available`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProfile();
      setLoading(false);
    };
    loadData();
  }, [user?.id]);

  useEffect(() => {
    if (profile) {
      fetchTasks();
    }
  }, [profile]);

  const handleApply = async (values: any) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.apiUrl}/api/contributors/apply`,
        {
          type: values.type,
          certifications: values.certifications || [],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setApplyModalVisible(false);
      form.resetFields();
      await fetchProfile();
    } catch (error) {
      console.error('Failed to apply:', error);
    }
  };

  const handleAcceptTask = async (taskId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${config.apiUrl}/api/contributors/tasks/${taskId}/accept`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchTasks();
    } catch (error) {
      console.error('Failed to accept task:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'green';
      case 'PENDING': return 'orange';
      case 'SUSPENDED': return 'red';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'blue';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{type.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
    },
    {
      title: 'Reward',
      key: 'reward',
      render: (record: ContributorTask) => (
        <div>
          <Text strong>{record.creditsReward} credits</Text>
          {record.paymentAmount && (
            <div>
              <Text type="success">${record.paymentAmount}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: ContributorTask) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleAcceptTask(record.id)}
        >
          Accept
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );
  }

  if (!profile) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <Card>
            <Title level={2}>Become an Expedient Contributor</Title>
            <Text>
              Join our network of data collectors, analysts, and field agents.
              Earn credits and money by completing tasks for farms.
            </Text>
            <div style={{ marginTop: '24px' }}>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => setApplyModalVisible(true)}
              >
                Apply Now
              </Button>
            </div>
          </Card>

          <Modal
            title="Apply as Contributor"
            open={applyModalVisible}
            onOk={() => form.submit()}
            onCancel={() => {
              setApplyModalVisible(false);
              form.resetFields();
            }}
          >
            <Form form={form} onFinish={handleApply} layout="vertical">
              <Form.Item
                name="type"
                label="Contributor Type"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select type">
                  <Option value="DATA_COLLECTOR">Data Collector</Option>
                  <Option value="ANALYST">Analyst</Option>
                  <Option value="VALIDATOR">Validator</Option>
                  <Option value="FIELD_AGENT">Field Agent</Option>
                </Select>
              </Form.Item>
              <Form.Item name="certifications" label="Certifications">
                <Select mode="tags" placeholder="Add certifications">
                  <Option value="USDA_CERTIFIED">USDA Certified</Option>
                  <Option value="ENERGY_AUDITOR">Energy Auditor</Option>
                  <Option value="AGRICULTURE_EXPERT">Agriculture Expert</Option>
                </Select>
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>Contributor Dashboard</Title>
          <Tag color={getStatusColor(profile.status)}>{profile.status}</Tag>
          {profile.verified && (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Verified
            </Tag>
          )}
        </div>

        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Tasks"
                value={profile.totalTasks}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Completed"
                value={profile.completedTasks}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Credits Earned"
                value={profile.creditsEarned}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Earnings"
                value={profile.totalEarnings}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Available Tasks">
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default ContributorDashboard;


